/**
 * Epic H · Billing, per-seat model: the coach pays for their students (H1).
 * Plus payment history with invoices (H3) and VAT/accountant export (H6).
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, getToken, ApiError } from '../../lib/api.ts';
import { useAuth } from '../../lib/auth.tsx';
import {
  Button,
  Card,
  EmptyState,
  ErrorText,
  Field,
  Input,
  Modal,
  Spinner,
  Tag,
  fmtDate,
  fmtMoney,
} from '../../lib/ui.tsx';

interface SubscriptionInfo {
  status: string;
  currentPeriodEnd: string | null;
  activeStudents: number;
  perStudentCents: number;
  freeStudents: number;
  monthlyCostCents: number;
  lastCharge: { createdAt: string; amountCents: number; invoice: { id: string; number: string } | null } | null;
}

interface PaymentRow {
  id: string;
  createdAt: string;
  kind: string;
  description: string;
  amountCents: number;
  status: string;
  payerName: string | null;
  platformFeeCents: number;
  invoice: { id: string; number: string } | null;
}

interface InvoiceDetail {
  id: string;
  number: string;
  issuedAt: string;
  sellerName: string;
  sellerVatId: string | null;
  buyerName: string;
  netCents: number;
  vatCents: number;
  grossCents: number;
  reverseCharge: boolean;
}

/** CSV/file downloads need the Authorization header → fetch as blob. */
async function downloadAuthed(path: string, filename: string) {
  const res = await fetch(path, { headers: { Authorization: `Bearer ${getToken()}` } });
  if (!res.ok) throw new ApiError(res.status, 'Download failed');
  const url = URL.createObjectURL(await res.blob());
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export default function BillingPage() {
  const { me } = useAuth();
  const qc = useQueryClient();
  const [toast, setToast] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [invoiceId, setInvoiceId] = useState<string | null>(null);

  const showToast = (msg: string) => {
    setToast(msg);
    window.setTimeout(() => setToast(null), 4000);
  };

  const sub = useQuery({
    queryKey: ['billing-subscription'],
    queryFn: () => api<SubscriptionInfo>('/api/billing/subscription'),
  });
  const payments = useQuery({
    queryKey: ['billing-payments'],
    queryFn: () => api<PaymentRow[]>('/api/billing/payments'),
  });
  const invoice = useQuery({
    queryKey: ['billing-invoice', invoiceId],
    queryFn: () => api<InvoiceDetail>(`/api/billing/invoices/${invoiceId}`),
    enabled: !!invoiceId,
  });

  const renewNow = useMutation({
    mutationFn: () =>
      api<{ chargedCents: number; invoiceNumber: string | null }>('/api/billing/subscription/renew', { body: {} }),
    onSuccess: (res) => {
      setError(null);
      showToast(
        res.chargedCents > 0
          ? `Renewed — ${fmtMoney(res.chargedCents)} charged (${res.invoiceNumber})`
          : 'Renewed — nothing to charge, your students are within the free allowance',
      );
      void qc.invalidateQueries({ queryKey: ['billing-payments'] });
      void qc.invalidateQueries({ queryKey: ['billing-subscription'] });
    },
    onError: setError,
  });

  // ── H6 · tax settings ──────────────────────────────────────────────────────
  const [vatId, setVatId] = useState(me?.coachProfile?.vatId ?? '');
  const [brandName, setBrandName] = useState(me?.coachProfile?.brandName ?? '');
  const saveVat = useMutation({
    mutationFn: () =>
      api('/api/billing/vat-settings', {
        method: 'PUT',
        body: { vatId: vatId.trim() || null, brandName: brandName.trim() || null },
      }),
    onSuccess: () => {
      setError(null);
      showToast('Tax settings saved');
    },
    onError: setError,
  });

  if (sub.isLoading) return <Spinner />;

  return (
    <div className="space-y-4">
      <h1 className="text-lg font-bold">Billing</h1>

      {/* ── H1 · Per-seat plan: you pay for your students ─────────────────── */}
      {sub.data && (
        <Card title="Your Solfo plan">
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="rounded-xl border border-slate-200 p-3 text-center">
              <p className="text-2xl font-bold">{sub.data.activeStudents}</p>
              <p className="text-xs text-slate-500">active students</p>
              <p className="mt-1 text-[11px] text-slate-400">first {sub.data.freeStudents} are free</p>
            </div>
            <div className="rounded-xl border border-slate-200 p-3 text-center">
              <p className="text-2xl font-bold">{fmtMoney(sub.data.perStudentCents)}</p>
              <p className="text-xs text-slate-500">per student / month</p>
              <p className="mt-1 text-[11px] text-slate-400">no tiers, no caps</p>
            </div>
            <div className="rounded-xl border border-brand-300 bg-brand-50/50 p-3 text-center ring-1 ring-brand-200">
              <p className="text-2xl font-bold text-brand-700">{fmtMoney(sub.data.monthlyCostCents)}</p>
              <p className="text-xs text-slate-500">your monthly total</p>
              <p className="mt-1 text-[11px] text-slate-400">
                {Math.max(0, sub.data.activeStudents - sub.data.freeStudents)} billable × {fmtMoney(sub.data.perStudentCents)}
              </p>
            </div>
          </div>
          <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
            <p className="text-xs text-slate-500">
              {sub.data.status} · renews automatically {fmtDate(sub.data.currentPeriodEnd)} at your student count on that day
              {sub.data.lastCharge &&
                ` · last charge ${fmtMoney(sub.data.lastCharge.amountCents)} on ${fmtDate(sub.data.lastCharge.createdAt)}`}
            </p>
            <Button variant="secondary" disabled={renewNow.isPending} onClick={() => renewNow.mutate()}>
              Renew now
            </Button>
          </div>
          <p className="mt-2 rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-500">
            Adding students never blocks — each active student beyond the free {sub.data.freeStudents} simply adds{' '}
            {fmtMoney(sub.data.perStudentCents)} to next month's charge. Archive a student to stop paying for them.
            Lesson fees themselves stay between you and your students, outside Solfo.
          </p>
        </Card>
      )}

      <ErrorText error={error} />

      {/* ── H3 · Payments & invoices ──────────────────────────────────────── */}
      <Card title="Payments">
        {payments.isLoading ? (
          <Spinner />
        ) : !payments.data || payments.data.length === 0 ? (
          <EmptyState title="No payments yet" hint="Your monthly per-student charges appear here with their invoices." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-xs text-slate-500">
                  <th className="py-2 pr-3 font-medium">Date</th>
                  <th className="py-2 pr-3 font-medium">Description</th>
                  <th className="py-2 pr-3 font-medium">Kind</th>
                  <th className="py-2 pr-3 text-right font-medium">Amount</th>
                  <th className="py-2 pr-3 font-medium">Status</th>
                  <th className="py-2 font-medium">Invoice</th>
                </tr>
              </thead>
              <tbody>
                {payments.data.map((p) => (
                  <tr key={p.id} className="border-b border-slate-100">
                    <td className="py-2 pr-3 whitespace-nowrap">{fmtDate(p.createdAt)}</td>
                    <td className="max-w-[280px] truncate py-2 pr-3">{p.description}</td>
                    <td className="py-2 pr-3">
                      <Tag>{p.kind.replace(/_/g, ' ')}</Tag>
                    </td>
                    <td className="py-2 pr-3 text-right font-medium whitespace-nowrap">{fmtMoney(p.amountCents)}</td>
                    <td className="py-2 pr-3">
                      <Tag tone={p.status === 'paid' ? 'green' : p.status === 'refunded' ? 'red' : 'amber'}>
                        {p.status}
                      </Tag>
                    </td>
                    <td className="py-2">
                      {p.invoice ? (
                        <button
                          className="text-xs font-medium text-brand-700 hover:underline"
                          onClick={() => setInvoiceId(p.invoice!.id)}
                        >
                          {p.invoice.number}
                        </button>
                      ) : (
                        <span className="text-xs text-slate-400">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* ── H6 · Tax & accountant export ──────────────────────────────────── */}
      <Card title="Tax settings">
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="VAT ID" hint="EU B2B invoices switch to reverse charge automatically.">
            <Input placeholder="DE123456789" value={vatId} onChange={(e) => setVatId(e.target.value)} />
          </Field>
          <Field label="Brand name on invoices">
            <Input placeholder="e.g. Studio Cantabile" value={brandName} onChange={(e) => setBrandName(e.target.value)} />
          </Field>
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          <Button disabled={saveVat.isPending} onClick={() => saveVat.mutate()}>
            Save
          </Button>
          <Button
            variant="secondary"
            onClick={() => void downloadAuthed('/api/billing/export.csv', 'solfo-accounting.csv').catch(setError)}
          >
            Download accountant CSV
          </Button>
        </div>
      </Card>

      {/* ── H3 · Invoice modal ────────────────────────────────────────────── */}
      <Modal open={!!invoiceId} onClose={() => setInvoiceId(null)} title="Invoice">
        {invoice.isLoading ? (
          <Spinner />
        ) : invoice.error ? (
          <ErrorText error={invoice.error} />
        ) : invoice.data ? (
          <div className="space-y-4">
            <div className="rounded-xl border border-slate-200 p-4">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-base font-bold text-brand-700">{invoice.data.sellerName}</p>
                  {invoice.data.sellerVatId && <p className="text-xs text-slate-500">VAT {invoice.data.sellerVatId}</p>}
                </div>
                <div className="text-right">
                  <p className="font-mono text-sm font-semibold">{invoice.data.number}</p>
                  <p className="text-xs text-slate-500">issued {fmtDate(invoice.data.issuedAt)}</p>
                </div>
              </div>
              <p className="mt-4 text-xs text-slate-500">Billed to</p>
              <p className="text-sm font-medium">{invoice.data.buyerName}</p>
              <table className="mt-4 w-full text-sm">
                <tbody>
                  <tr className="border-t border-slate-100">
                    <td className="py-1.5 text-slate-600">Net</td>
                    <td className="py-1.5 text-right">{fmtMoney(invoice.data.netCents)}</td>
                  </tr>
                  <tr className="border-t border-slate-100">
                    <td className="py-1.5 text-slate-600">VAT</td>
                    <td className="py-1.5 text-right">{fmtMoney(invoice.data.vatCents)}</td>
                  </tr>
                  <tr className="border-t border-slate-200 font-semibold">
                    <td className="py-1.5">Total</td>
                    <td className="py-1.5 text-right">{fmtMoney(invoice.data.grossCents)}</td>
                  </tr>
                </tbody>
              </table>
              {invoice.data.reverseCharge && (
                <p className="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800">
                  Reverse charge — VAT due by recipient.
                </p>
              )}
            </div>
          </div>
        ) : null}
      </Modal>

      {toast && (
        <div className="fixed bottom-4 left-1/2 z-50 -translate-x-1/2 rounded-lg bg-slate-900 px-4 py-2 text-sm text-white shadow-lg">
          {toast}
        </div>
      )}
    </div>
  );
}
